import { useRef, useState, useEffect } from 'react';
import * as faceapi from 'face-api.js';
import { FACE_API_MODELS_URL } from 'src/onboarding/utils/faceApiPreload';
import './FaceIDScanner.scss';
import { ReactComponent as BackIcon } from '../../assets/images/arrow-left-white.svg';
import { Text } from 'src/onboarding/components/typography/Text/Text';
import { t } from '@web-solutions/module-localization';
import { useNav } from 'src/onboarding/hooks/nav';
import Analytics from '@web-solutions/module-analytics';

// Геометрия рамки (круга) — единая для отрисовки, детекции и кропа фото
const getFrameGeometry = (w: number, h: number) => {
  const radius = Math.min(w, h) * 0.35;
  return { radius, cx: w / 2, cy: 80 + radius };
};

// Пропорции блока с фото юзера на dynamic-personal-plan:
// calc((100vw - PHOTO_BLOCK_SIDE_GAP) / 2) x PHOTO_BLOCK_HEIGHT
const PHOTO_BLOCK_HEIGHT = 248;
const PHOTO_BLOCK_SIDE_GAP = 48;
// Немного воздуха вокруг круга, чтобы лицо не упиралось в края кропа
const FRAME_PADDING = 1.1;

const getPhotoAspect = () => {
  const blockWidth = (window.innerWidth - PHOTO_BLOCK_SIDE_GAP) / 2;
  return blockWidth / PHOTO_BLOCK_HEIGHT;
};

// Рамка-круг в координатах исходного кадра камеры: video на экране растянуто
// как object-fit: cover и отражено (transform: scaleX(-1))
const getFrameInVideoCoords = (video: HTMLVideoElement, screenW: number, screenH: number) => {
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  const { cx, cy, radius } = getFrameGeometry(screenW, screenH);

  const coverScale = Math.max(screenW / vw, screenH / vh);
  const offsetX = (screenW - vw * coverScale) / 2;
  const offsetY = (screenH - vh * coverScale) / 2;

  return {
    frameCx: vw - (cx - offsetX) / coverScale,
    frameCy: (cy - offsetY) / coverScale,
    frameRadius: radius / coverScale,
  };
};

// Кроп под пропорции блока: круг целиком влезает по меньшей стороне,
// по большей добираем контекст, и не выходим за пределы кадра камеры
const getFrameCropRect = (video: HTMLVideoElement) => {
  const vw = video.videoWidth;
  const vh = video.videoHeight;

  const { frameCx, frameCy, frameRadius } = getFrameInVideoCoords(
    video,
    window.innerWidth,
    window.innerHeight
  );

  const side = frameRadius * 2 * FRAME_PADDING;
  const aspect = getPhotoAspect();

  let cropW = aspect >= 1 ? side * aspect : side;
  let cropH = aspect >= 1 ? side : side / aspect;

  const fit = Math.min(1, vw / cropW, vh / cropH);
  cropW *= fit;
  cropH *= fit;

  return {
    cropX: Math.min(Math.max(frameCx - cropW / 2, 0), vw - cropW),
    cropY: Math.min(Math.max(frameCy - cropH / 2, 0), vh - cropH),
    cropW,
    cropH,
  };
};

function FaceIDScanner({
  onPhotoTaken,
  onClose,
  stream,
  preloadFaceIdModels = true,
  cropFaceIdPhotoToFrame = false,
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const animationRef = useRef<number | null>(null);
  const detectIntervalRef = useRef<number | null>(null);

  const { onNext } = useNav();

  const [cameraReady, setCameraReady] = useState(false);
  const [scanFinished, setScanFinished] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);

  const [shape, setShape] = useState<'square' | 'circle'>('square');

  const firstFaceDetected = useRef(false);
  const scanFinishedRef = useRef(false);
  const photoTimerRef = useRef<number | null>(null);
  const modelsLoadedRef = useRef(preloadFaceIdModels);

  useEffect(() => {
    // Загружаем модели только если они не были предзагружены на странице
    if (preloadFaceIdModels) {
      modelsLoadedRef.current = true;
      return;
    }

    const initializeModels = async () => {
      try {
        await faceapi.nets.tinyFaceDetector.loadFromUri(FACE_API_MODELS_URL);

        // Pre-warm WASM/GPU с фиктивной детекцией для быстрого старта
        try {
          const tempCanvas = document.createElement('canvas');
          tempCanvas.width = 224;
          tempCanvas.height = 224;
          await faceapi.detectSingleFace(
            tempCanvas as any,
            new faceapi.TinyFaceDetectorOptions({ inputSize: 224 })
          );
        } catch {
          // Игнорируем ошибки pre-warming
        }

        modelsLoadedRef.current = true;
      } catch (error) {
        console.error('Error loading face detection models:', error);
        modelsLoadedRef.current = true; // Устанавливаем true чтобы не зависнуть
      }
    };

    initializeModels();
  }, [preloadFaceIdModels]);

  useEffect(() => {
    if (!stream) return;

    const video = videoRef.current!;
    const canvas = canvasRef.current!;
    const dpr = window.devicePixelRatio || 1;
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;

    const setupCanvas = () => {
      canvas.width = screenWidth * dpr;
      canvas.height = screenHeight * dpr;
      const ctx = canvas.getContext('2d');
      if (ctx) ctx.scale(dpr, dpr);
      setCameraReady(true);
    };

    // Установи размеры canvas сразу, не ждём видео
    setupCanvas();

    video.srcObject = stream;

    video.onloadedmetadata = () => {
      video.play().catch(() => {});
    };
  }, [stream]);

  // анимация + детекция
  useEffect(() => {
    if (!cameraReady) return;

    const takePhoto = () => {
      const video = videoRef.current!;
      const vw = video.videoWidth;
      const vh = video.videoHeight;

      const tempCanvas = document.createElement('canvas');
      const ctx = tempCanvas.getContext('2d')!;

      if (cropFaceIdPhotoToFrame && vw && vh) {
        // Обрезаем по рамке-кругу под пропорции блока на dynamic-personal-plan
        const { cropX, cropY, cropW, cropH } = getFrameCropRect(video);

        tempCanvas.width = Math.round(cropW);
        tempCanvas.height = Math.round(cropH);

        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        ctx.drawImage(video, cropX, cropY, cropW, cropH, 0, 0, tempCanvas.width, tempCanvas.height);
      } else {
        // Полный кадр камеры — поведение до появления кропа
        tempCanvas.width = vw;
        tempCanvas.height = vh;

        ctx.drawImage(video, 0, 0, tempCanvas.width, tempCanvas.height);
      }

      tempCanvas.toBlob((blob) => {
        if (blob) {
          const file = new File([blob], 'face.png', { type: 'image/png' });
          onPhotoTaken(file);
        }
      });
    };

    const canvas = canvasRef.current!;
    const ctx = canvas.getContext('2d')!;
    const video = videoRef.current!;

    let inside = false;
    let currentSegment = 0;
    const totalSegments = 140;

    let shapeProgress = 0;
    let shapeStartTime: number | null = null;

    const easeInOutCubic = (t: number) =>
      t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

    // детекция лица
    detectIntervalRef.current = window.setInterval(async () => {
      if (scanFinishedRef.current || !modelsLoadedRef.current) return; // после завершения скана или если модели не загружены

      const detection = await faceapi.detectSingleFace(
        video,
        new faceapi.TinyFaceDetectorOptions({
          inputSize: 224,
          scoreThreshold: 0.4,
        })
      );

      if (!detection) {
        inside = false;
        return;
      }

      const dpr = window.devicePixelRatio || 1;
      const logicalCanvasWidth = canvas.width / dpr;
      const logicalCanvasHeight = canvas.height / dpr;

      const resized = faceapi.resizeResults(detection, {
        width: logicalCanvasWidth,
        height: logicalCanvasHeight,
      });

      const { x, y, width, height } = resized.box;

      const minFaceSize = logicalCanvasWidth * 0.28;
      if (width < minFaceSize || height < minFaceSize) {
        inside = false;
        return;
      }

      const faceX = x + width / 2;
      const faceY = y + height / 2;

      const { radius, cx, cy } = getFrameGeometry(logicalCanvasWidth, logicalCanvasHeight);

      const dx = faceX - cx;
      const dy = faceY - cy;
      const distance = Math.sqrt(dx * dx + dy * dy);

      const isCentered = distance < radius * 0.9;

      if (!isCentered) {
        inside = false;
        return;
      }

      if (!firstFaceDetected.current) {
        firstFaceDetected.current = true;

        if (photoTimerRef.current) {
          clearTimeout(photoTimerRef.current);
        }

        photoTimerRef.current = window.setTimeout(() => {
          takePhoto();
          Analytics.trackEvent('photo', 'taken');
        }, 1500);

        setShape('circle');
      }

      inside = true;
    }, 120);

    const dpr = window.devicePixelRatio || 1;
    const animate = (timestamp: number) => {
      const w = canvas.width / dpr;
      const h = canvas.height / dpr;
      // Radius scales with screen size, top edge of the circle is 80px from top
      const { radius, cx, cy } = getFrameGeometry(w, h);

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.clearRect(0, 0, w, h);

      ctx.fillStyle = 'rgb(0,0,0)';
      ctx.fillRect(0, 0, w, h);

      // прогресс перехода квадрат → круг
      if (shape === 'circle' && !shapeStartTime) {
        shapeStartTime = timestamp;
      }
      ctx.save();
      ctx.globalCompositeOperation = 'destination-out';
      ctx.beginPath();

      if (shapeStartTime) {
        const elapsed = timestamp - shapeStartTime;
        const rawProgress = Math.min(elapsed / 1200, 1);
        shapeProgress = easeInOutCubic(rawProgress);
      }

      const squareSize = radius * 2;
      const squareX = cx - squareSize / 2;
      const squareY = cy - squareSize / 2;

      const startR = squareSize * 0.1;
      const endR = radius;
      const borderR = startR + (endR - startR) * shapeProgress;

      // если скан ещё идёт — классическая маска (прозрачный круг, видно живое видео под канвасом)
      if (!scanFinishedRef.current) {
        ctx.save();
        ctx.globalCompositeOperation = 'destination-out';
        ctx.beginPath();

        if (shapeProgress < 1) {
          const r = borderR;

          ctx.moveTo(squareX + r, squareY);
          ctx.lineTo(squareX + squareSize - r, squareY);
          ctx.quadraticCurveTo(squareX + squareSize, squareY, squareX + squareSize, squareY + r);
          ctx.lineTo(squareX + squareSize, squareY + squareSize - r);
          ctx.quadraticCurveTo(
            squareX + squareSize,
            squareY + squareSize,
            squareX + squareSize - r,
            squareY + squareSize
          );
          ctx.lineTo(squareX + r, squareY + squareSize);
          ctx.quadraticCurveTo(squareX, squareY + squareSize, squareX, squareY + squareSize - r);
          ctx.lineTo(squareX, squareY + r);
          ctx.quadraticCurveTo(squareX, squareY, squareX + r, squareY);
        } else {
          ctx.arc(cx, cy, radius, 0, Math.PI * 2);
        }

        ctx.fill();
        ctx.restore();
      }

      ctx.globalCompositeOperation = 'source-over';

      // штрихи + завершение скана
      if (shapeProgress === 1 && !scanFinishedRef.current) {
        if (inside && currentSegment < totalSegments) {
          currentSegment += 0.5;
        }

        if (currentSegment >= totalSegments && !scanFinishedRef.current) {
          scanFinishedRef.current = true;
          setScanFinished(true);

          setTimeout(() => {
            onNext();
            onClose();
          }, 2000);
        }

        for (let i = 0; i < totalSegments; i++) {
          const progress = i < currentSegment;
          const strokeGap = radius * 0.04;
          const strokeLengthInactive = radius * 0.1;
          const strokeLengthActive = radius * 0.17;
          const strokeDistance = radius + strokeGap + strokeLengthInactive / 2;

          const segmentAngle = (Math.PI * 2 * i) / totalSegments;

          const x1 = cx + Math.cos(segmentAngle) * strokeDistance;
          const y1 = cy + Math.sin(segmentAngle) * strokeDistance;

          const x2 = cx + Math.cos(segmentAngle) * (strokeDistance + (progress ? strokeLengthActive : strokeLengthInactive));
          const y2 = cy + Math.sin(segmentAngle) * (strokeDistance + (progress ? strokeLengthActive : strokeLengthInactive));

          ctx.strokeStyle = progress ? 'rgba(0, 206, 0, 1)' : 'rgba(138, 138, 138, 1)';
          ctx.lineWidth = radius * 0.027;
          ctx.lineCap = 'round';

          ctx.beginPath();
          ctx.moveTo(x1, y1);
          ctx.lineTo(x2, y2);
          ctx.stroke();
        }
      }

      // режим после завершения скана: рисуем размытую картинку ВНУТРИ круга + зелёные штрихи
      if (scanFinishedRef.current) {
        // 1. рисуем видео внутри круга
        ctx.save();
        ctx.beginPath();
        ctx.arc(cx, cy, radius, 0, Math.PI * 2);
        ctx.clip();

        // Рисуем видео как object-fit: cover (заполняет область сохраняя пропорции)
        const videoAspect = video.videoWidth / video.videoHeight;
        const screenAspect = w / h;

        let drawWidth: number, drawHeight: number;
        if (videoAspect > screenAspect) {
          // Видео шире экрана
          drawHeight = h;
          drawWidth = h * videoAspect;
        } else {
          // Видео уже экрана
          drawWidth = w;
          drawHeight = w / videoAspect;
        }

        const offsetX = (w - drawWidth) / 2;
        const offsetY = (h - drawHeight) / 2;

        ctx.translate(offsetX + drawWidth, offsetY);
        ctx.scale(-1, 1);
        ctx.drawImage(video, 0, 0, drawWidth, drawHeight);
        ctx.restore();

        // 2. затемняем область внутри круга
        ctx.save();
        ctx.beginPath();
        ctx.arc(cx, cy, radius, 0, Math.PI * 2);
        ctx.clip();
        ctx.fillStyle = 'rgba(0,0,0,0.45)';
        ctx.fillRect(0, 0, w, h);
        ctx.restore();

        // 3. зелёный контур
        ctx.save();
        ctx.beginPath();
        ctx.arc(cx, cy, radius + radius * 0.08, 0, Math.PI * 2);
        ctx.strokeStyle = 'rgba(0,206,0,1)';
        ctx.lineWidth = radius * 0.032;
        ctx.stroke();
        ctx.restore();
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      firstFaceDetected.current = false;
      if (detectIntervalRef.current) clearInterval(detectIntervalRef.current);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      if (photoTimerRef.current) {
        clearTimeout(photoTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraReady, shape]);

  if (permissionDenied) return null;

  return (
    <div className='face-scanner-overlay'>
      <button
        className='face-scanner-close'
        onClick={() => {
          onClose();
          Analytics.trackEvent('face-scan-id-animation-back', 'click');
        }}
      >
        <BackIcon />
      </button>

      <video ref={videoRef} playsInline autoPlay muted className='face-scanner-video' />

      <canvas ref={canvasRef} className='face-scanner-canvas' />

      <Text size='m' medium className='face-scanner-desc'>
        {scanFinished
          ? t('onboarding.faceScanId.complete')
          : shape === 'circle'
            ? t('onboarding.faceScanId.makeMotion')
            : t('onboarding.faceScanId.placeYourFace')}
      </Text>

      {scanFinished && (
        <div className='container-circle' style={{ zIndex: 102 }}>
          <div className='cicrlceBg'></div>
          <div className='circle'></div>
          <svg className='checkmark' viewBox='0 0 52 52'>
            <path
              className='checkmarkPath'
              fill='none'
              stroke='#00CE00'
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth='6'
              d='M14 27 l10 10 l18 -18'
            />
          </svg>
        </div>
      )}
    </div>
  );
}

export default FaceIDScanner;
